import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output} from '@angular/core';
import {BehaviorSubject, combineLatestWith, map, Observable, Subject, takeUntil} from 'rxjs';
import {
	ConfigurationItem,
	ConfigurationRequest,
	FreeConfigurationCsvItem,
	FreeConfigurationPdf
} from '@app/model/configuration.model';
import {UserInfo} from '@app/model/user-info.model';
import {LocalizedText} from '@app/model/equipment.model';
import {UserInfoService} from '@app/service/user-info.service';
import {ShoppingCartItem, ShoppingCartService} from '@app/service/shopping-cart.service';
import {ArticleService} from '@app/service/article.service';
import {AlertService} from '@app/shared/service/alert.service';
import {LocalizeService} from '@app/service/localize.service';
import {MatDialog} from '@angular/material/dialog';
import {
	SaveConfigurationDialogComponent
} from '@app/shared/components/save-configuration-dialog/save-configuration-dialog.component';

@Component({
	selector: 'app-shopping-cart',
	templateUrl: './shopping-cart.component.html',
	styleUrls: ['./shopping-cart.component.scss']
})
export class ShoppingCartComponent implements OnInit, OnDestroy {
	@Input() mobile$!: Observable<boolean>;
	@Output() closeShoppingCart = new EventEmitter<void>();

	shoppingCartItems$: Observable<ShoppingCartItem[]>;
	numberOfItems$: Observable<number>;

	totalCost$!: Observable<number>;

	readonly userInfo$: Observable<UserInfo>;
	readonly withPrices$!: Observable<boolean>;
	readonly isLoggedIn$: Observable<boolean>;

	private readonly languageShortCode: keyof LocalizedText;

	readonly #updateTotalCost$ = new BehaviorSubject<void>(undefined);
	readonly #destroyed$ = new Subject<void>();

	constructor(private userInfoService: UserInfoService,
							private shoppingCartService: ShoppingCartService,
							private articleService: ArticleService,
							private alertService: AlertService,
							private localizeService: LocalizeService,
							@Inject(LOCALE_ID) private locale: string,
							public dialog: MatDialog) {
		this.userInfo$ = userInfoService.getUserInfo$();
		this.withPrices$ = this.userInfo$.pipe(map(userInfo => userInfo.roles.includes('READ_PRICES')));
		this.isLoggedIn$ = this.userInfo$.pipe(map(userInfo => !userInfo.anonymous));

		this.languageShortCode = locale.substring(0, 2).toLowerCase() as keyof LocalizedText;

		this.shoppingCartItems$ = this.shoppingCartService.articleList$;
		this.numberOfItems$ = shoppingCartService.articleList$.pipe(
			map(items =>
				items.reduce((accumulator, item) => accumulator + (item.quantity.value), 0)
			)
		);
	}

	ngOnInit(): void {
		this.totalCost$ = this.shoppingCartItems$.pipe(
			combineLatestWith(this.#updateTotalCost$),
			map(([a,_]) => a),
			map(configuration => configuration.map(item => (item.article.price || 0) * (item.quantity.value))),
			map((prices: number[]) => prices.reduce((a, b) => a + b, 0))
		);
	}

	updateTotalCost(): void {
		this.#updateTotalCost$.next();
	}

	ngOnDestroy(): void {
		this.#destroyed$.next();
		this.#destroyed$.complete();
		this.#updateTotalCost$.complete();
	}

	removeItemFromCart(item: ShoppingCartItem): void {
		this.shoppingCartService.removeConfigurationItem(item.article.articleNumber);
	}

	resetCart(): void {
		this.shoppingCartService.resetConfiguration();
	}

	async exportCsv(items: ShoppingCartItem[]): Promise<void> {
		const exportItems: FreeConfigurationCsvItem[] = items.map(item => ({
			articleNumber: item.article.articleNumber ?? '',
			quantity: item.quantity.value
		}));

		await this.articleService.exportCsv(exportItems);
	}

	async exportPdf(items: ShoppingCartItem[], date: Date = new Date()): Promise<void> {
		const model: FreeConfigurationPdf = {
			title: $localize`Freie Konfiguration`,
			dateTime: date.toLocaleString(),
			disclaimer: $localize`Achtung: Die ausgewählten Geräte passen möglicherweise nicht zusammen!`,
			header: {
				articleNumber: $localize`Artikel Nr.`,
				designation: $localize`Bezeichnung`,
				designationOtherLanguage: $localize`Bezeichnung EN`,
				packageSize: $localize`Verpackungsgröße`,
				quantity: $localize`Menge`
			},
			items: items.map(item => ({
				articleNumber: item.article.articleNumber ?? '',
				quantity: item.quantity.value,
				designation: this.localizeService.localize(item.article.designation),
				// If user language is non-English, use English for alternate designation.
				// If user language is English, fall back to German for alternate designation.
				designationOtherLanguage: this.languageShortCode === 'en'
					? item.article.designation?.de ?? null
					: item.article.designation?.en ?? null,
				packageSize: item.article.packageSize ?? null
			}))
		};

		await this.articleService.exportPdf(model);
	}

	openSaveArticleListDialogue(items: ShoppingCartItem[]): void {
		const configurationItems = items.map(item => ({
			articleNumber: item.article.articleNumber,
			quantity: item.quantity.value
		} as ConfigurationItem));

		const saveArticleListDialogRef = this.dialog.open(SaveConfigurationDialogComponent, {
			data: {
				userInfo$: this.userInfo$
			},
			maxHeight: '90vh'
		});

		saveArticleListDialogRef.afterClosed().pipe(takeUntil(this.#destroyed$)).subscribe(result => {
			if (result === undefined) {
				return;
			}

			const request: ConfigurationRequest = {
				items: configurationItems,
				name: result.name,
				description: result.description,
				access: result.access ?? 'CREATOR_USER',
				equipmentCode: undefined
			};

			this.shoppingCartService.saveArticleList(request)
				.pipe(takeUntil(this.#destroyed$))
				.subscribe(async () => {
					this.alertService.alertInfo($localize`Konfiguration erfolgreich gespeichert`);
				});
		});
	}
}
